import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { makePair, Pair } from '../../../../../adl-gen/sys/types';
import { AnswerScores, I18nContent } from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { SelectBox, SelectOption } from '../../../../../ui/widgets/select/select';
import { Table, TableCell, TableRow } from '../../../../../ui/widgets/table/table';
import {
  FieldState,
  FormValue,
  newIntegerFieldState,
  withValueCallback
} from '../../../../../ui/widgets/text-input/text-field';
import { WithId } from '../../../../../util/utils';
import { AnswerType, TextInputObserver } from '../../../common/editor';

const styles = require('../../../common/editor.css');
const listStyles = require('../../../common/list.css');
const fonts = require('../../../../../ui/common/fonts.css');

/// TODO Amalgamate/consolidate all these type defs and interface defs
type MultiResponseInputScore = WithId<Pair<number, FieldState<number>>>;

interface MultiResponseInputScores {
  scores: MultiResponseInputScore[];
}

interface ScoringInput {
  kind: AnswerType;
  value: MultiResponseInputScores;
}

interface MultiResponseEditorProps extends WithI18nProps {
  debug?: boolean;
  lang?: LangCode;
  name: string;
  content: I18nContent;
  answers: WithId<I18nContent>[];
  scoring?: AnswerScores;

  postUpdates(answers: AnswerScores): void;

  onCancel(): void;
}

/**
 * Base component to create/update MutliResponse answers.
 * To impose some sense of order in this, we will be opinionated about the score
 * map. So the index in the scores array will always equal the left-hand value
 * of the corresponding Pair for the score
 * ie. [score0, score1, score2, ...] -> [ {v1: 0, v2: score-for-answer-0}, {v1: 1, v2: score-for-answer-1}, {v1: 2, ...}, ... ]
 * In the case of MultiResponse, all answers must have a score
 */
@observer
class MultiResponseEditor0 extends React.Component<MultiResponseEditorProps> {
  @observable inputScores: ScoringInput;

  @observable lang: LangCode;

  constructor(props: Readonly<MultiResponseEditorProps>) {
    super(props);
    this.lang = props.lang ? props.lang : props.currentLangCode();
    const { answers, scoring } = props;

    const scores: MultiResponseInputScore[] = [];

    if (scoring) {
      if (scoring.kind !== 'multiResponse') {
        // Something has gone wrong
        throw new Error(`MultiResponseEditor: incompatible answer type ${scoring.kind}`);
      }
    }

    answers.map((answer, index) => {
      /// Possibly sparse array of scores... at most one per answer...
      /// We'll push that into a packed array including possibly empty scores
      /// The ID is required to match answers while they are in memory, in case they
      /// move around or are deleted - keep the scores with the answers
      const inputScore = {
        id: answer.id,
        value: makePair({
          v1: index,
          v2: withValueCallback(newIntegerFieldState(0, 100), this.onChange)
        })
      };

      scores.push(inputScore);
    });
    /// Scoring is now automatically sorted

    this.inputScores = {
      kind: 'multiResponse',
      value: { scores }
    };
    /// Now initialize - this triggers onSave so it's important the structure is
    /// complete before we initialize
    if (scoring && scoring.kind === 'multiResponse') {
      scoring.value.scores.map(score => {
        /// score.v1 = index into answer array, not answer ID
        /// Don't assume the scores are in a logical order
        const inputScore = this.inputScores.value.scores.find(sc => sc.value.v1 === score.v1);
        if (inputScore) {
          /// Should be of course
          inputScore.value.v2.onChange(`${score.v2}`);
        }
      });
    }
  }

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  @action
  onChange = (_score: FormValue<number>) => {
    const scoresToPost: AnswerScores = { kind: 'multiResponse', value: { scores: [] } };
    /// Get the input value in full - have to run this filter
    if (this.inputScores.kind === 'multiResponse') {
      // AnswerScore: Reset the whole lot - this is because "splicing" is
      // unreliable when each keystroke can update a target value, and answers
      // cann be reordered or deleted
      const scores = this.inputScores.value.scores.reduce((acc: Pair<number, number>[], next, idx) => {
        const v2 = next.value.v2.getValid();
        if (v2 !== undefined) {
          /// Update v1 in case answers reordered since last time
          acc.push(makePair({ v1: idx, v2 }));
        }
        return acc;
      }, []);
      scoresToPost.value.scores = scores;
    }
    /// Post
    this.onSave(scoresToPost);
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
        title={'Language'}
        xsSpan={2}
      />
    );
  });

  AnswerScoreObserver = observer((prop: { idx: number }) =>
    TextInputObserver({
      field: this.inputScores.value.scores[prop.idx].value.v2,
      type: 'number',
      title: '', //`Score for answer ${prop.v1 + 1}`,
      placeholder: 'eg. 33',
      xsSpan: 2,
      debug: this.props.debug
    })
  );

  @computed
  get inputIsValid(): boolean {
    let isValid: boolean = true;
    // const {  answers } = this.props
    // const { scores } = this.inputScores.value
    // if (answers.length > 0) {
    //   // Should never happen
    //   if (scores.length !== answers.length) { return false }
    //   isValid = scores.every(score => {
    //     if (score.v2.getValid() !== undefined) return false
    //     return !isEmpty(score.v2) && isNumeric(score.v2.value())
    //   })
    // }
    return isValid;
  }

  @action
  onSave = (scoresToPost: AnswerScores) => {
    const { postUpdates } = this.props;
    if (this.inputIsValid) {
      postUpdates(scoresToPost);
    }
  };

  renderHeaderRow = () => {
    return (
      <TableRow header>
        <TableCell>
          <div>ANSWER TEXT</div>
        </TableCell>
        <TableCell>
          <div>ANSWER SCORE</div>
        </TableCell>
      </TableRow>
    );
  };

  Edit = () => {
    const { answers, content, t } = this.props;
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            <this.LangSelector />
          </div>
          <div className={styles.row}>
            <span className={fonts.caption}>{t(content, this.lang)}</span>
          </div>
          <div className={styles.row}>
            <Table column3VisibleAfter="sm" className={listStyles.noBorder}>
              {this.renderHeaderRow()}
              {answers.map(answer => {
                const idx = this.inputScores.value.scores.findIndex(score => score.id === answer.id);
                if (idx >= 0) {
                  /// Should always do
                  return (
                    <TableRow key={answer.id}>
                      <TableCell>
                        <span className={fonts.caption}>{t(answer.value, this.lang)}</span>
                      </TableCell>
                      <TableCell>
                        <this.AnswerScoreObserver idx={idx} />
                      </TableCell>
                    </TableRow>
                  );
                } else {
                  return null;
                }
              })}
            </Table>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { name } = this.props;
    const title = `Answers for question ${name} (${this.inputScores.kind})`;
    return (
      <React.Fragment>
        <h4>{title}</h4>
        <this.Edit />
      </React.Fragment>
    );
  }
}

export const MultiResponseEditor = withI18n(MultiResponseEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/answers-scoring-editors/multi-response-scores-editor.tsx